<template>
  <Chart
    v-if="isLoaded && Highcharts"
    :highcharts="Highcharts"
    constructor-type="mapChart"
    :options="chartOptions"
  />
</template>
<script>
import {getHighchartsChart} from '@/services/highchartsService';
import capitalizeFirstLetter from '@/mixins/capitalizeFirstLetter';

export default {
  name: 'WorldMap',
  components: {
    Chart: () => import('highcharts-vue').then(m => m.Chart)
  },
  mixins: [capitalizeFirstLetter],
  props: {
    continentData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      Highcharts: null,
      isLoaded: false,
      chartOptions: {
        chart: {
          events: {
            load: function () {
              const chart = this;
              const series = chart.series && chart.series[0];
              if (!series) return;

              chart.defaultPoint = series.points.find(p => p && p.code === 'default')
                || series.points.find(p => p && p.value != null)
                || series.points[0];

              if (chart.tooltip && !chart.tooltip._hidePatched) {
                chart.tooltip._originalHide = chart.tooltip.hide;
                chart.tooltip.hide = function () {
                };
                chart.tooltip._hidePatched = true;
              }

              if (chart.defaultPoint) chart.tooltip.refresh(chart.defaultPoint);
            },
            render: function () {
              const chart = this;
              const series = chart.series && chart.series[0];
              if (!series) return;
              chart.defaultPoint = series.points.find(p => p && p.code === 'default')
                || series.points.find(p => p && p.value != null)
                || series.points[0];
              if (chart.defaultPoint) chart.tooltip.refresh(chart.defaultPoint);
            }
          }
        },

        tooltip: {
          useHTML: true,
          borderWidth: 1,
          borderColor: '#8CB5FF',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          shadow: true,
          padding: 12,
          stickOnContact: true,
          positioner: function () {
            const chart = this.chart;
            return {
              x: 0,
              y: chart.plotHeight / 1.5,
            };
          },
          formatter: function () {
            const series = this.series;
            if (!series) return '';

            const points = series.points || [];
            const byRegion = new Map();

            points.forEach(p => {
              let name = p.name;
              if (p.isEuropeGroup) name = 'Europe';
              else if (p.isAsiaGroup) name = 'Asia';
              if (!byRegion.has(name)) byRegion.set(name, p.value);
            });

            const order = ['Europe', 'Asia', 'Canada', 'United States of America', 'Default'];

            const getColor = (nm) => {
              const fn = series.chart.options.series[0].getSquareColor;
              return fn ? fn(nm) : '#757575';
            };

            const rows = [];

            order.forEach(name => {
              if (byRegion.has(name)) {
                const val = byRegion.get(name);
                if (val != null && val !== 0) {
                  rows.push(rowHtml(name, val, getColor(name)));
                }
              }
            });

            Array.from(byRegion.keys()).forEach(name => {
              if (!order.includes(name)) {
                const val = byRegion.get(name);
                if (val != null && val !== 0) {
                  rows.push(rowHtml(name, val, getColor(name)));
                }
              }
            });

            if (!rows.length) return '<b>No data</b>';

            return `<div style="min-width: 240px;">
              <div style="font-weight: 600; margin-bottom: 6px;">Regions</div>
              ${rows.join('')}
              </div>`;

            function rowHtml(name, val, color) {
              return `
                  <div style="display:flex;align-items:center;gap:8px;margin:2px 0;">
                    <div style="width:10px;height:10px;background:${color};border-radius:2px"></div>
                    <span style="font-weight:600">${name}</span>
                    <span style="margin-left:6px">${val}%</span>
                  </div>`;
            }
          }
        },
        plotOptions: {
          series: {
            enableMouseTracking: false,
            states: {hover: {enabled: false}}
          }
        },
        title: {text: null},
        colorAxis: {visible: false, stops: [[0, '#f7f7f7']]},
        legend: {layout: 'vertical', align: 'left', verticalAlign: 'bottom'},
        series: [
          {
            data: [],
            mapData: null,
            joinBy: ['hc-key', 'code'],
            name: 'Percent',
            getSquareColor(continentName) {
              const colorMap = {
                'Europe': '#67B1B8',
                'Asia': '#AEC7ED',
                'Canada': '#5F97D4',
                'United States of America': '#082655',
                'Default': '#757575',
              };
              return colorMap[continentName] || '#757575';
            },
            states: {hover: {color: '#f7f7f7'}},
            dataLabels: {
              allowOverlap: true,
              crop: false,
              overflow: 'allow',
              enabled: true,
              useHTML: true,
              formatter: function () {
                if (this.point.isEuropeGroup && this.point.code !== 'de') {
                  return '';
                }
                if (this.point.isAsiaGroup && this.point.code !== 'cn') {
                  return '';
                }
                if (this.point.code === 'default') {
                  return '';
                }

                if (this.point.value !== null && this.point.value !== undefined && this.point.value > 0) {
                  let colorKey = this.point.name;
                  if (this.point.isEuropeGroup) {
                    colorKey = 'Europe';
                  } else if (this.point.isAsiaGroup) {
                    colorKey = 'Asia';
                  }

                  const squareColor = this.series.chart.options.series[0].getSquareColor
                    ? this.series.chart.options.series[0].getSquareColor(colorKey)
                    : '#757575';

                  return `
                    <div style="
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      background-color: #fff;
                      border: 1px solid #8CB5FF;
                      border-radius: 8px;
                      padding: 8px;
                      box-sizing: border-box;
                      box-shadow: 0px 4px 4px 0px #A0ACC240;
                    ">
                      <div style="
                        width: 16px;
                        height: 16px;
                        background-color: ${squareColor};
                        border-radius: 4px;
                        flex-shrink: 0;
                      "></div>
                      <span style="font-weight: bold; font-size: 11px;">${this.point.value}%</span>
                    </div>
                  `;
                } else {
                  return '';
                }
              },
              style: {
                fontSize: '11px',
                color: '#000',
                fontWeight: 'bold',
                textOutline: 'none'
              },
            }
          }
        ]
      },
    };
  },
  watch: {
    continentData: {
      immediate: true,
      handler(newData) {
        this.chartOptions.series[0].data = this.prepareSeriesData(newData);
      }
    }
  },
  async mounted() {
    const {Highcharts} = await getHighchartsChart();
    this.Highcharts = Highcharts;
    const world = await import(
      /* webpackChunkName: "charts" */
      '@highcharts/map-collection/custom/world-lowres.geo.json'
      );

    this.chartOptions.chart.map = world.default || world;
    this.chartOptions.series[0].mapData = world.default || world;
    this.chartOptions.series[0].data = this.prepareSeriesData(this.continentData);

    this.isLoaded = true;
  },
  methods: {
    getContinentCode(regionName) {
      const codeMap = {
        'europe': 'europe-group',
        'asia': 'asia-group',
        'canada': 'ca',
        'america': 'us',
        'default': 'default'
      };
      return codeMap[regionName] || regionName.toLowerCase();
    },

    getEuropeanCountries() {
      return [
        'de', 'fr', 'it', 'es', 'gb', 'pl', 'nl', 'be', 'at', 'ch',
        'se', 'no', 'dk', 'fi', 'ie', 'pt', 'gr', 'cz', 'hu', 'ro',
        'bg', 'hr', 'sk', 'si', 'ee', 'lv', 'lt', 'lu', 'mt', 'cy'
      ];
    },

    getAsianCountries() {
      return [
        'cn', 'jp', 'in', 'kr', 'th', 'vn', 'my', 'sg', 'ph', 'id',
        'tw', 'kz', 'uz', 'kg', 'tj', 'tm', 'af', 'pk', 'bd', 'lk',
        'np', 'bt', 'mm', 'la', 'kh', 'mn'
      ];
    },
    prepareSeriesData(newData) {
      const mappedData = [];

      newData.forEach(item => {
        if (item.continent === 'europe') {
          this.getEuropeanCountries().forEach(countryCode => {
            mappedData.push({
              name: 'Europe',
              code: countryCode,
              value: item.value,
              isEuropeGroup: true,
              isAsiaGroup: false
            });
          });
        } else if (item.continent === 'asia') {
          this.getAsianCountries().forEach(countryCode => {
            mappedData.push({
              name: 'Asia',
              code: countryCode,
              value: item.value,
              isEuropeGroup: false,
              isAsiaGroup: true
            });
          });
        } else {
          mappedData.push({
            name: this.capitalizeFirstLetter(item.continent),
            code: this.getContinentCode(item.continent),
            value: item.value,
            isEuropeGroup: false,
            isAsiaGroup: false
          });
        }
      });

      return mappedData;
    },
  },
};
</script>

<style scoped>
</style>